import React, { useState } from "react";

import { Typography, Box, Alert, Tab, Tabs } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../api/client";
import { ComplaintListResponse, ComplaintItem } from "../api/PartnerPortalGatewayApi";
import { MainLayout } from "../components/layout/MainLayout";
import { CardGrid, FeedbackCard, ExcludedChildCard } from "../components/operational";
import { CACard } from "../components/ui";
import type { RootState } from "../store";
import { useAppSelector } from "../store/hooks";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`operational-info-tabpanel-${index}`}
      aria-labelledby={`operational-info-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

export const OperationalInformationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Complaints pagination state
  const [complaintsPage, setComplaintsPage] = useState(0);
  const [complaintsPageSize, setComplaintsPageSize] = useState(25);
  const complaintsSortBy = "Created";
  const complaintsSortDesc = true;

  // Excluded children pagination state
  const [excludedPage, setExcludedPage] = useState(0);
  const [excludedPageSize, setExcludedPageSize] = useState(25);

  const selectedService = useAppSelector((state: RootState) => state.service.selectedService);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Complaints/Feedback Query
  const {
    data: complaintsData,
    isLoading: complaintsLoading,
    error: complaintsError,
  } = useQuery<ComplaintListResponse>({
    queryKey: [
      "complaints",
      selectedService?.serviceId,
      complaintsPage,
      complaintsPageSize,
      complaintsSortBy,
      complaintsSortDesc,
    ],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        throw new Error("No service selected");
      }
      return await apiClient.operationalInformation.getComplaintList(
        selectedService.serviceId,
        complaintsPage + 1, // Convert 0-based to 1-based for API
        complaintsPageSize,
        complaintsSortBy,
        complaintsSortDesc,
      );
    },
    enabled: !!selectedService?.serviceId && activeTab === 0,
    staleTime: 0,
    gcTime: 0,
  });

  // Excluded Children Query
  const {
    data: excludedChildrenData,
    isLoading: excludedChildrenLoading,
    error: excludedChildrenError,
  } = useQuery<any>({
    queryKey: ["excludedChildren", selectedService?.serviceId, excludedPage, excludedPageSize],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        throw new Error("No service selected");
      }
      return await apiClient.operationalInformation.getExcludedChildren(
        selectedService.serviceId,
        excludedPage, // Keep 0-based for Child API
        excludedPageSize,
      );
    },
    enabled: !!selectedService?.serviceId && activeTab === 1,
    staleTime: 0,
    gcTime: 0,
  });

  // Event handlers for pagination
  const handleComplaintsPageChange = (page: number) => {
    setComplaintsPage(page);
  };

  const handleComplaintsPageSizeChange = (pageSize: number) => {
    setComplaintsPageSize(pageSize);
  };

  const handleExcludedPageChange = (page: number) => {
    setExcludedPage(page);
  };

  const handleExcludedPageSizeChange = (pageSize: number) => {
    setExcludedPageSize(pageSize);
  };

  if (!selectedService) {
    return (
      <MainLayout>
        <Alert severity="warning">Please select a service to view operational information.</Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <CACard>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="operational information tabs">
            <Tab label="Feedback" id="operational-info-tab-0" />
            <Tab label="Excluded Children" id="operational-info-tab-1" />
          </Tabs>
        </Box>

        {/* Feedback Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ padding: { xs: 0, sm: 1, md: 2 } }}>
            <Typography variant="h6" gutterBottom>
              Feedback
            </Typography>
            {complaintsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error loading feedback: {(complaintsError as Error).message}
              </Alert>
            )}
            <CardGrid
              items={complaintsData?.data || []}
              renderCard={(feedback: ComplaintItem) => <FeedbackCard feedback={feedback} />}
              loading={complaintsLoading}
              totalCount={complaintsData?.total || 0}
              page={complaintsPage}
              pageSize={complaintsPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={handleComplaintsPageChange}
              onPageSizeChange={handleComplaintsPageSizeChange}
              emptyMessage="You have no feedback recorded against your service."
              getItemKey={(feedback: ComplaintItem) => feedback.complaintId || 0}
            />
          </Box>
        </TabPanel>

        {/* Excluded Children Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ padding: { xs: 0, sm: 1, md: 2 } }}>
            <Typography variant="h6" gutterBottom>
              Excluded Children
            </Typography>
            {excludedChildrenError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error loading excluded children: {(excludedChildrenError as Error).message}
              </Alert>
            )}
            <CardGrid
              items={excludedChildrenData?.data || []}
              renderCard={(child: any) => <ExcludedChildCard child={child} />}
              loading={excludedChildrenLoading}
              totalCount={excludedChildrenData?.totalRecords || 0}
              page={excludedPage}
              pageSize={excludedPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={handleExcludedPageChange}
              onPageSizeChange={handleExcludedPageSizeChange}
              emptyMessage="You have no excluded children."
              getItemKey={(child: any, index: number) => child.childId || index}
            />
          </Box>
        </TabPanel>
      </CACard>
    </MainLayout>
  );
};
